import { Expand, Forward } from "components/common/Icons";
import { useState, useEffect } from "react";
import { TYPO } from "styles/Typo";
import { STRATEGY_META, STRATEGY_TYPE } from "utils/constant/STATUS";
import { calculrateReturnByLogReturn } from "./PerformanceCharts";
import { useRouter } from "next/router";
import { MONTH_ENG } from "utils/constant/ETC";
import { useTranslation } from "next-i18next";
import StrategyRecommandModal from "components/modals/StrategyRecommandModal";

function StrategyProfile({
  sCode,
  logMock,
  risk,
  changeTime,
  assetCount,
  profileRef,
  setFaqAutoExpanded,
  realtimeData,
}: {
  sCode: STRATEGY_TYPE;
  logMock: any;
  risk: number;
  changeTime: string;
  assetCount: number;
  profileRef: React.RefObject<HTMLDivElement>;
  setFaqAutoExpanded: (faqAutoExpanded: boolean) => void;
  realtimeData: any;
}) {
  const router = useRouter();
  const { t } = useTranslation("strategy");

  const realTimeLogMean = realtimeData?.assets?.length
    ? realtimeData.assets.reduce(
        (acc: number, curr: any) => acc + curr?.PRICE_CHG_LOG,
        0
      ) / realtimeData?.assets?.length
    : null;
  const entireReturn = calculrateReturnByLogReturn(
    [...logMock.map((log: any) => log?.DAILY_CHG_LOG), realTimeLogMean]?.filter(
      (log: any) => log !== null
    )
  );

  const startDate = logMock[0]?.BASE_DT;
  let year = startDate?.split("T")[0].split?.("-")?.[0]?.slice(2),
    month = Number(startDate?.split("T")[0].split("-")[1]),
    day = Number(startDate?.split("T")[0].split("-")[2]);

  // 투자매력도 설명 auto scroll
  const scrollToInvestmentAttractDesc = () => {
    const faq = document.querySelector(".faq");
    if (faq) {
      faq.scrollIntoView({ behavior: "smooth" });
    }
  };

  const converPeriodEng = (period: string) => {
    switch (period) {
      case "2주":
        return "Bi-weekly";
      case "1개월":
        return "Monthly";
      case "3개월":
        return "Quarterly";
      default:
        return "";
    }
  };

  const BasicInfo = () => {
    // 전략명
    // 전략 코드
    // 전체기간 수익률
    // 거래시작일
    return (
      <div className="flex flex-col px-6 pt-[30px] gap-2">
        <div className="flex items-center gap-1">
          <span className={` ${TYPO.TYPO5_1_MD}  text-gray80 break-keep`}>
            {router.locale === "ko"
              ? STRATEGY_META[sCode].name
              : STRATEGY_META[sCode].engName}
          </span>
          <div className="px-[6px] bg-gray10 rounded-[8px] flex items-center justify-center">
            <span className={`${TYPO.TYPO7_MD} text-gray50`}>
              {sCode.toUpperCase()}
            </span>
          </div>
        </div>
        <div className={`${TYPO.TYPO2_SB} flex items-center gap-1`}>
          <span className={`text-gray80`}>
            {router.locale === "ko" ? "오늘까지" : "Up to today"}
          </span>
          <span
            className={`st-entire-return ${
              entireReturn > 0
                ? "text-red40"
                : entireReturn === 0
                ? "texg-gray60"
                : "text-blue40"
            }`}
          >
            {entireReturn > 0 ? "+" : ""}
            {entireReturn.toFixed(1)}%
          </span>
        </div>
        <span className={`${TYPO.TYPO7_MD} text-gray50`}>
          {router.locale === "ko"
            ? `${year}. ${month}. ${day}부터 거래`
            : `Trading since ${MONTH_ENG[Number(month) - 1]}. ${day}, ${
                startDate?.split("T")[0].split?.("-")?.[0]
              }`}
        </span>
      </div>
    );
  };
  const DetailInfo = () => {
    // 운영 종목 개수
    // 종목 교체 주기
    // 리스크
    return (
      <div className="flex flex-col py-2 gap-2">
        <div className="items-center flex gap-3 py-2 px-6">
          <span className={`${TYPO.TYPO5_1_MD} text-gray50 grow`}>
            {t("stocksInOperation")}
          </span>
          <div className="flex flex-col items-end">
            <span className={`${TYPO.TYPO5_1_SB} text-gray80`}>{`${assetCount}${
              router.locale === "ko" ? "개" : " stocks"
            }`}</span>
            <span className={`${TYPO.TYPO7_1_MD} text-gray40`}>
              {router.locale === "ko"
                ? changeTime
                : converPeriodEng(changeTime)}
              {router.locale === "ko" ? " 주기로 교체" : " rebalancing"}
            </span>
          </div>
        </div>

        <div className="items-center flex gap-3 py-2 px-6">
          <span className={`${TYPO.TYPO5_1_MD} text-gray50 grow`}>
            {t("strategyRisk")}
          </span>
          <span className={`${TYPO.TYPO5_1_SB} text-blue40`}>
            {router.locale === "ko" ? "최대" : "Max."} {`${risk}%`}
          </span>
        </div>
      </div>
    );
  };

  const Recommand = () => {
    const [isModalOpen, setModalOpen] = useState(false);
    return (
      <div className={`py-2  gap-2 h-fit border-t-gray2 border-t-[1px]`}>
        <div
          className="px-6 py-2 gap-3 items-center flex cursor-pointer "
          onClick={() => {
            window.dataLayer.push({
              event: "cStratDescription",
            });
            setModalOpen(!isModalOpen);
          }}
        >
          <div className="w-[40px] h-[40px] relative">
            <Icon />
          </div>
          <div className="flex flex-col grow">
            <span className={`${TYPO.TYPO5_1_MD}  text-gray80`}>
              {router.locale === "ko"
                ? `${STRATEGY_META[sCode].recommandType} 투자자에게 추천해요`
                : `Recommended for ${STRATEGY_META[sCode].recommandTypeEng}`}
            </span>
          </div>
          <Forward fill="gray30" height={16} width={16} />
        </div>

        {isModalOpen && (
          <StrategyRecommandModal
            sCode={sCode}
            setModalOpen={setModalOpen}
            isModalOpen={isModalOpen}
          />
        )}
      </div>
    );
  };

  const DetailInfoV2 = () => {
    const targetStrateMeta = STRATEGY_META[sCode];
    const col = [
      { title: "매달 첫 영업일에" },
      {
        title: targetStrateMeta.standard,
        description: targetStrateMeta.standardSub,
      },
      { title: `투자매력도 높은 종목 ${assetCount}개에 투자해요` },
    ];

    // useEffect(() => {
    // const num1 = document.querySelector(".strate-num-1");
    // const num2 = document.querySelector(".strate-num-2");
    // const num3 = document.querySelector(".strate-num-3");
    // const line1 = document.querySelector(".strate-line-1");
    // const line2 = document.querySelector(".strate-line-2");
    // const num1Rect = num1?.getBoundingClientRect();
    // const num2Rect = num2?.getBoundingClientRect();
    // const num3Rect = num3?.getBoundingClientRect();
    // const num1RectTop = num1Rect?.top;
    // const num2RectTop = num2Rect?.top;
    // const num3RectTop = num3Rect?.top;
    // if (line1 && line2 && num1RectTop && num2RectTop && num3RectTop) {
    //   (line1 as HTMLElement).style.height = `${
    //     num2RectTop - num1RectTop - 30 - 16
    //   }px`;
    //   (line2 as HTMLElement).style.height = `${
    //     num3RectTop - num2RectTop - 30 - 16
    //   }px`;
    // }
    // }, []);
    const Section = ({
      number,
      title,
      description,
    }: {
      number: number;
      title: string;
      description?: string;
    }) => {
      return (
        <div
          className={`flex  px-6 pt-2 gap-3 items-stretch ${
            number === 3 ? "cursor-pointer" : ""
          }`}
          data-gtm-id={number === 3 ? "stInvestmentAttractDesc" : ""}
          onClick={() => {
            if (number === 3) {
              if (router.query.type === "holdings") {
                router.replace({ query: { type: "performance" } }, undefined, {
                  shallow: true,
                });
                setTimeout(() => {
                  scrollToInvestmentAttractDesc();
                  setTimeout(() => {
                    setFaqAutoExpanded(true);
                  }, 800);
                }, 100);
                return;
              } else {
                scrollToInvestmentAttractDesc();
                setTimeout(() => {
                  setFaqAutoExpanded(true);
                }, 700);
              }
            }
          }}
        >
          {/** number + line */}
          <div className="flex flex-col gap-1 items-center">
            <div
              className={`strate-num-${number} rounded-full bg-gray10 flex items-center justify-center h-[30px] w-[30px] relative`}
            >
              <p className={`${TYPO.TYPO7_1_MD} text-gray60`}>{number}</p>
              {/* {number < 3 && (
                <div
                  className={`strate-line-${number} w-[1px] h-[10px] bg-gray20 absolute bottom-[-8px] translate-y-[100%] left-[50%] translate-x-[-50%]`}
                />
              )} */}
            </div>
            <div className="py-[2px] grow flex-1">
              {number < 3 && (
                <div
                  className={`strate-line-${number} w-[2px]  h-full bg-gray15 rounded-[4px] `}
                />
              )}
            </div>
          </div>

          {/** title + description */}
          <div
            className="flex flex-col gap-1 pt-[2px]"
            style={{
              paddingBottom:
                number === 1 ? "24px" : number === 2 ? "16px" : "0px",
            }}
          >
            <p className={`${TYPO.TYPO5_1_MD} text-gray80`}>{title}</p>
            {description && (
              <p className={`${TYPO.TYPO7_RG} text-gray50`}>{description}</p>
            )}
            {number === 3 && (
              <div className="flex items-center gap-1">
                <span className={`${TYPO.TYPO7_RG} text-blue40`}>
                  {router.locale === "ko" ? "투자매력도란?" : "View more"}
                </span>
                <Forward fill="blue40" height={12} width={12} />
              </div>
            )}
          </div>
          {/* {number === 3 && <Forward fill="gray30" height={16} width={16} />} */}
        </div>
      );
    };
    return (
      <div className="flex flex-col">
        {col.map((item, index) => (
          <Section
            key={index}
            number={index + 1}
            title={item.title}
            description={item.description}
          />
        ))}
      </div>
    );
  };

  return (
    <div className="flex flex-col gap-4 pb-4" ref={profileRef}>
      <BasicInfo />
      <DetailInfoV2 />
      {/* <DetailInfo /> */}
      {/* <Recommand /> */}
    </div>
  );
}

const Icon = () => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="40"
      height="40"
      viewBox="0 0 40 40"
      fill="none"
    >
      <rect
        x="17.2734"
        y="5.45312"
        width="5.45455"
        height="6.36364"
        rx="2.72727"
        fill="#FFCA45"
      />
      <rect
        x="4.54492"
        y="16.3594"
        width="6.36364"
        height="9.09091"
        rx="3.18182"
        fill="#F49943"
      />
      <rect
        x="29.0898"
        y="16.3594"
        width="6.36364"
        height="9.09091"
        rx="3.18182"
        fill="#F49943"
      />
      <rect
        x="16.3633"
        y="12.7266"
        width="5.45455"
        height="7.27273"
        rx="1.81818"
        transform="rotate(-90 16.3633 12.7266)"
        fill="#F49943"
      />
      <rect
        x="8.18164"
        y="9.09375"
        width="23.6364"
        height="24.5455"
        rx="4.375"
        fill="#FFE062"
      />
      <rect
        x="15.9082"
        y="24.5469"
        width="8.18182"
        height="2.72727"
        rx="1.36364"
        fill="white"
      />
      <rect
        x="22.2734"
        y="17.2734"
        width="3.63636"
        height="3.63636"
        rx="1.81818"
        fill="var(--gray90)"
      />
      <rect
        x="14.0898"
        y="17.2734"
        width="3.63636"
        height="3.63636"
        rx="1.81818"
        fill="var(--gray90)"
      />
    </svg>
  );
};

export default StrategyProfile;
